import { AddressFormValue } from '@/components/address/address-fields'
import { costPerService, totalMonthlyCost } from '@/lib/pricing/service-cost'
import {
    OnboardingCost,
    OnboardingProfile,
    OnboardingSpecialty,
    OnboardingSurgery,
} from '@/lib/api/onboarding/onboarding-api'

/**
 * Etapas do assistente. Clínica/hospital salta direto de `profile` para `review`:
 * a personalização (especialidade, custo, valor da consulta, cirurgia) descreve o
 * trabalho de UM médico, e numa clínica quem responde não é quem atende.
 */
export type OnboardingStep =
    | 'profile'
    | 'doctor'
    | 'specialties'
    | 'costs'
    | 'pricing'
    | 'surgeries'
    | 'review'
    /**
     * Etapa PÓS-conclusão: o assistente já gravou tudo. Não entra em
     * `DOCTOR_STEPS`/`CLINIC_STEPS` de propósito — não é um passo a preencher, e
     * contá-la na barra de progresso faria a última etapa parecer inacabada.
     */
    | 'digitalAccount'

export const DOCTOR_STEPS: OnboardingStep[] = [
    'profile',
    'doctor',
    'specialties',
    'costs',
    'pricing',
    'surgeries',
    'review',
]

export const CLINIC_STEPS: OnboardingStep[] = ['profile', 'review']

export const STEP_TITLES: Record<OnboardingStep, string> = {
    profile: 'Vamos personalizar sua experiência',
    doctor: 'Seus dados e onde você atende',
    specialties: 'Suas especialidades',
    costs: 'Vamos entender seus custos',
    pricing: 'Quanto você cobra pela consulta',
    surgeries: 'Cirurgias que você mais realiza',
    review: 'Tudo pronto',
    digitalAccount: 'Falta só ativar sua conta digital',
}

export interface OnboardingFormState {
    profile: OnboardingProfile | null
    doctor: {
        name: string
        registrationNumber: string
        councilName: string
        document: string
        phoneNumber: string
        /**
         * Mesmo formato do bloco `AddressFields` (o do cadastro de parceiro,
         * profissional e hospital) — inclusive `cityCode`, o IBGE que a consulta
         * de CEP preenche e a NFS-e exige.
         */
        address: AddressFormValue
    }
    specialties: OnboardingSpecialty[]
    costs: OnboardingCost[]
    /** Consultas atendidas num mês típico — é o divisor do custo. */
    monthlyConsultations: number
    isSurgeon: boolean
    surgeries: OnboardingSurgery[]
}

export const emptyFormState = (): OnboardingFormState => ({
    profile: null,
    doctor: {
        name: '',
        registrationNumber: '',
        councilName: 'CRM',
        document: '',
        phoneNumber: '',
        address: {
            label: 'Consultório',
            street: '',
            number: '',
            complement: '',
            neighborhood: '',
            city: '',
            cityCode: '',
            state: '',
            zipCode: '',
            country: 'BR',
        },
    },
    specialties: [],
    costs: [],
    monthlyConsultations: 0,
    isSurgeon: false,
    surgeries: [],
})

/**
 * Termos que dizem, na própria especialidade, que ali se opera.
 *
 * A pergunta "você é cirurgião?" nasce **desmarcada**: a maioria dos médicos não
 * opera, e marcar por conta própria cadastraria cirurgia em conta de quem só
 * consulta. A exceção é a especialidade trazer a palavra — "Cirurgia Geral",
 * "Neurocirurgia", "Cirurgia Plástica" —, e aí a resposta já está no que o médico
 * escreveu.
 *
 * A lista é deliberadamente curta. Ortopedia, Ginecologia e Oftalmologia estavam
 * aqui e marcavam o clínico que nunca entrou em centro cirúrgico: essas
 * especialidades têm cirurgião e têm quem só atende no consultório, e a palavra
 * "cirurgia" é o único sinal que separa os dois sem chutar.
 */
const SURGICAL_SPECIALTY_HINTS = ['cirurg']

const normalize = (value: string): string =>
    (value ?? '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .trim()

export function looksSurgical(specialties: OnboardingSpecialty[]): boolean {
    return specialties.some(specialty => {
        const name = normalize(specialty.name)
        return SURGICAL_SPECIALTY_HINTS.some(hint => name.includes(hint))
    })
}

/**
 * Soma e rateio vêm de `lib/pricing/service-cost`: a mesma conta é oferecida no
 * catálogo (botão de calcular ao lado do preço de custo), e duas cópias dela dariam
 * dois resultados para a mesma pergunta. Os nomes locais continuam porque é assim
 * que as etapas do assistente falam — "consulta", não "atendimento".
 */
export const totalCosts = (costs: OnboardingCost[]): number => totalMonthlyCost(costs)

export const costPerConsultation = (
    costs: OnboardingCost[],
    monthlyConsultations: number
): number => costPerService(costs, monthlyConsultations)

/** Markup (%) sobre o CUSTO: `(venda − custo) / custo`. */
export function markupPercent(cost: number, sellingPrice: number): number | null {
    if (!cost || !sellingPrice) return null
    return ((sellingPrice - cost) / cost) * 100
}

/**
 * Margem de lucro (%) sobre a VENDA: `(venda − custo) / venda`.
 *
 * Não é o markup, e a diferença não é detalhe: 100% de markup é 50% de margem.
 * As duas aparecem lado a lado porque o médico pensa em uma e o relatório mostra
 * a outra.
 */
export function profitMarginPercent(cost: number, sellingPrice: number): number | null {
    if (!sellingPrice) return null
    return ((sellingPrice - cost) / sellingPrice) * 100
}

export function stepsFor(profile: OnboardingProfile | null): OnboardingStep[] {
    return profile === 'CLINIC' ? CLINIC_STEPS : DOCTOR_STEPS
}
